import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  UserCog,
  Search,
  Eye,
  AlertCircle,
  Loader2,
  ChevronLeft,
  ChevronRight,
} from 'lucide-react';
import { toast } from 'sonner';
import { adminClientService, AdminClient } from '../../services/adminClientService';
import { useAdmin } from '../../context/AdminContext';

/**
 * Pannello "Impersonazione cliente" — versione 2026-02 (rev. paginazione).
 *
 * Mostra una lista paginata di anagrafiche NTS (sola lettura). Ogni
 * riga ha 3 colonne: ``an_conto``, ragione sociale, provincia.
 *
 * QUALSIASI cliente è impersonabile: il backend crea on-the-fly uno
 * "shadow user" per le anagrafiche che non hanno account web associato
 * (vedi ``POST /api/auth/impersonate/anagrafica/{an_conto}``).
 */
const PAGE_SIZE = 25;

export default function AdminImpersonatePage() {
  const navigate = useNavigate();
  const { startImpersonation, impersonatedClient, isImpersonating, getAdminToken } = useAdmin();
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [page, setPage] = useState(1);
  const [items, setItems] = useState<AdminClient[]>([]);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Debounce 350ms — reset pagina quando cambia il filtro
  useEffect(() => {
    const t = setTimeout(() => {
      setDebouncedSearch(search.trim());
      setPage(1);
    }, 350);
    return () => clearTimeout(t);
  }, [search]);

  useEffect(() => {
    let cancelled = false;
    setIsLoading(true);
    setError(null);
    // Se un'impersonazione è già attiva, l'istanza axios condivisa usa
    // il token del cliente impersonato (non admin) → l'endpoint
    // /api/admin/clients risponderebbe 403. Recuperiamo un token admin
    // di backup (via refresh) per questa chiamata specifica.
    (async () => {
      try {
        const authToken = impersonatedClient ? (await getAdminToken()) || undefined : undefined;
        if (cancelled) return;
        const res = await adminClientService.list({ q: debouncedSearch || undefined, page, limit: PAGE_SIZE, authToken });
        if (cancelled) return;
        setItems(res.items);
        setTotalPages(res.pages || 1);
        setTotalCount(res.total || 0);
      } catch {
        if (!cancelled) setError('Impossibile caricare la lista clienti.');
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [debouncedSearch, page, impersonatedClient, getAdminToken]);

  const handleImpersonate = async (client: AdminClient) => {
    try {
      await startImpersonation(client.an_conto, client.company);
      toast.success(`Ora stai operando come ${client.company}`);
      navigate('/');
    } catch (e: any) {
      const msg = e?.response?.data?.detail || 'Errore durante l\'impersonazione';
      toast.error(typeof msg === 'string' ? msg : 'Impersonazione fallita');
    }
  };

  const visible = useMemo(() => items, [items]);
  const fromIdx = (page - 1) * PAGE_SIZE + 1;
  const toIdx = Math.min(page * PAGE_SIZE, totalCount);

  return (
    <div className="p-6" data-testid="admin-impersonate-page">
      <div className="flex items-center gap-3 mb-6">
        <div className="w-10 h-10 rounded-xl bg-red-100 flex items-center justify-center">
          <UserCog className="w-5 h-5 text-red-600" />
        </div>
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Impersonazione cliente</h1>
          <p className="text-sm text-muted-foreground">
            Accedi al portale come uno specifico cliente — il catalogo userà i
            suoi sconti, prezzi e budget.
          </p>
        </div>
      </div>

      {impersonatedClient && (
        <div className="rounded-xl border border-red-200 bg-red-50 p-3 mb-4 text-sm text-red-800 flex items-center gap-2" data-testid="impersonate-current-warning">
          <AlertCircle className="w-4 h-4 flex-shrink-0" />
          Stai già impersonando <strong>{impersonatedClient.name}</strong>. Selezionando un nuovo cliente l'impersonazione corrente verrà sostituita.
        </div>
      )}

      <div className="rounded-xl border bg-white">
        <div className="p-3 border-b">
          <div className="relative max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Cerca per ragione sociale, P.IVA, C.F., città..."
              data-testid="impersonate-search-input"
              className="w-full h-9 pl-9 pr-3 rounded-lg border text-sm focus:outline-none focus:ring-2 focus:ring-red-400"
            />
          </div>
        </div>

        {error ? (
          <div className="p-6 text-sm text-red-700 flex items-center gap-2">
            <AlertCircle className="w-4 h-4" /> {error}
          </div>
        ) : isLoading ? (
          <div className="p-12 text-center text-muted-foreground text-sm">
            <Loader2 className="w-5 h-5 animate-spin mx-auto mb-2" /> Caricamento clienti...
          </div>
        ) : visible.length === 0 ? (
          <div className="p-12 text-center text-muted-foreground text-sm">
            Nessun cliente corrisponde al filtro.
          </div>
        ) : (
          <>
            <table className="w-full text-sm" data-testid="impersonate-table">
              <thead className="bg-secondary/30 text-[10px] uppercase tracking-wider text-muted-foreground">
                <tr>
                  <th className="px-4 py-2 text-left font-semibold">an_conto</th>
                  <th className="px-4 py-2 text-left font-semibold">Nome cliente</th>
                  <th className="px-4 py-2 text-left font-semibold">Provincia</th>
                  <th className="px-4 py-2 text-right font-semibold">Azione</th>
                </tr>
              </thead>
              <tbody>
                {visible.map((c) => {
                  const isCurrent = impersonatedClient?.anConto === c.an_conto;
                  return (
                    <tr
                      key={c.id}
                      className="border-b hover:bg-secondary/40 transition-colors"
                      data-testid={`impersonate-row-${c.id}`}
                    >
                      <td className="px-4 py-3 font-mono text-xs">{c.an_conto}</td>
                      <td className="px-4 py-3 font-medium">{c.company}</td>
                      <td className="px-4 py-3 text-muted-foreground">{c.province || '—'}</td>
                      <td className="px-4 py-3 text-right">
                        {isCurrent ? (
                          <span className="text-[10px] inline-flex items-center gap-1 px-2 py-1 rounded bg-red-100 text-red-700 font-semibold">
                            <Eye className="w-3 h-3" /> ATTIVO
                          </span>
                        ) : (
                          <button
                            onClick={() => handleImpersonate(c)}
                            disabled={isImpersonating}
                            data-testid={`impersonate-btn-${c.id}`}
                            className="h-7 px-3 rounded-md text-[10px] font-medium inline-flex items-center gap-1 bg-red-500 text-white hover:bg-red-600 disabled:opacity-50 transition-colors"
                          >
                            <Eye className="w-3 h-3" /> Impersona
                          </button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>

            {/* Paginazione */}
            <div className="flex items-center justify-between p-3 border-t text-xs text-muted-foreground">
              <span data-testid="impersonate-pagination-info">
                {totalCount > 0
                  ? `Risultati ${fromIdx}-${toIdx} di ${totalCount}`
                  : '0 risultati'}
              </span>
              <div className="flex items-center gap-1">
                <button
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  disabled={page <= 1 || isLoading}
                  data-testid="impersonate-prev-page"
                  className="h-7 w-7 rounded border flex items-center justify-center hover:bg-secondary/40 disabled:opacity-30"
                  aria-label="Pagina precedente"
                >
                  <ChevronLeft className="w-3 h-3" />
                </button>
                <span className="px-2 font-medium text-foreground" data-testid="impersonate-page-current">
                  {page} / {totalPages}
                </span>
                <button
                  onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                  disabled={page >= totalPages || isLoading}
                  data-testid="impersonate-next-page"
                  className="h-7 w-7 rounded border flex items-center justify-center hover:bg-secondary/40 disabled:opacity-30"
                  aria-label="Pagina successiva"
                >
                  <ChevronRight className="w-3 h-3" />
                </button>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
